import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Edit2, Printer, BookOpen, ClipboardList, Users,
  TrendingUp, Loader2, Phone, MapPin, Mail, GraduationCap, ChevronRight,
  CheckCircle, Key, User, Briefcase, Calendar,
} from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '../../components/ui/badge';
import api from '../../api';

const TABS = ['Overview', 'Classes', 'Assignments'];

export default function TeacherDetailPage() {
  const { teacherId } = useParams();
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('Overview');

  useEffect(() => {
    setLoading(true);
    api.get(`/teachers/${teacherId}/summary`)
      .then(d => { if (d.success) setSummary(d.data); })
      .catch(err => toast.error(err?.detail || 'Failed to load teacher'))
      .finally(() => setLoading(false));
  }, [teacherId]);

  const handlePrint = () => {
    if (!summary) return;
    const { teacher } = summary;
    const fullName = `${teacher.first_name} ${teacher.last_name}`;
    const pw = window.open('', '_blank', 'width=800,height=1000');
    const html = `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <title>Teacher Profile – ${fullName}</title>
  <style>
    * { margin:0; padding:0; box-sizing:border-box; }
    body { font-family:'Segoe UI',Arial,sans-serif; background:#fff; color:#1e293b; font-size:13px; }
    .card { max-width:680px; margin:20px auto; border:1.5px solid #e2e8f0; border-radius:12px; overflow:hidden; }
    .header { background:linear-gradient(135deg,#4f46e5 0%,#7c3aed 100%); color:white; padding:24px 28px; display:flex; align-items:center; gap:20px; }
    .avatar { width:72px; height:72px; border-radius:12px; border:3px solid rgba(255,255,255,.6); background:#818cf8; display:flex; align-items:center; justify-content:center; font-size:24px; font-weight:800; color:white; flex-shrink:0; }
    .header-text h1 { font-size:22px; font-weight:700; margin-bottom:4px; }
    .header-text p { opacity:.8; font-size:13px; }
    .kpis { display:grid; grid-template-columns:repeat(3,1fr); gap:1px; background:#e2e8f0; border-bottom:1px solid #e2e8f0; }
    .kpi { background:#f8fafc; padding:12px 16px; text-align:center; }
    .kpi-val { font-size:18px; font-weight:700; color:#1e293b; }
    .kpi-label { font-size:10px; color:#64748b; text-transform:uppercase; letter-spacing:.5px; margin-top:2px; }
    .section { padding:20px 28px; border-bottom:1px solid #f1f5f9; }
    .section h2 { font-size:10px; text-transform:uppercase; letter-spacing:1px; color:#94a3b8; font-weight:700; margin-bottom:12px; }
    .grid2 { display:grid; grid-template-columns:1fr 1fr; gap:8px 24px; }
    .field { display:flex; justify-content:space-between; align-items:center; padding:6px 0; border-bottom:1px solid #f1f5f9; }
    .field-label { color:#64748b; font-size:11px; font-weight:600; }
    .field-value { font-weight:600; color:#1e293b; font-size:12px; }
    .tag { display:inline-block; background:#eef2ff; color:#4f46e5; border-radius:6px; padding:2px 8px; font-size:11px; font-weight:600; margin:2px 3px 2px 0; }
    .footer { padding:10px 28px; text-align:center; font-size:10px; color:#94a3b8; background:#f8fafc; }
    @media print { body { -webkit-print-color-adjust:exact; print-color-adjust:exact; } }
  </style>
</head>
<body>
  <div class="card">
    <div class="header">
      ${teacher.avatar
        ? `<img src="${teacher.avatar}" style="width:72px;height:72px;border-radius:12px;border:3px solid rgba(255,255,255,.6);object-fit:cover;flex-shrink:0" />`
        : `<div class="avatar">${(teacher.first_name?.[0]||'')+(teacher.last_name?.[0]||'')}</div>`
      }
      <div class="header-text">
        <h1>${fullName}</h1>
        <p>${teacher.employee_number || ''} · ${teacher.employment_type || 'Full-time'}</p>
        <p>${(teacher.subjects||[]).join(' · ') || 'No subjects assigned'}</p>
      </div>
    </div>
    <div class="kpis">
      <div class="kpi"><div class="kpi-val">${summary.class_count}</div><div class="kpi-label">Classes</div></div>
      <div class="kpi"><div class="kpi-val">${summary.student_count}</div><div class="kpi-label">Students</div></div>
      <div class="kpi"><div class="kpi-val">${summary.my_class ? summary.my_class.name : 'None'}</div><div class="kpi-label">Class Teacher</div></div>
    </div>
    <div class="section">
      <h2>Personal Information</h2>
      <div class="grid2">
        <div>
          ${[['First Name',teacher.first_name],['Last Name',teacher.last_name],['Gender',teacher.gender||'—'],['Qualification',teacher.qualification||'—']].map(([l,v])=>`<div class="field"><span class="field-label">${l}</span><span class="field-value">${v}</span></div>`).join('')}
        </div>
        <div>
          ${[['Email',teacher.email],['Phone',teacher.phone||'—'],['Employment',teacher.employment_type||'—'],['Employee No.',teacher.employee_number||'—']].map(([l,v])=>`<div class="field"><span class="field-label">${l}</span><span class="field-value">${v}</span></div>`).join('')}
        </div>
      </div>
      <div style="margin-top:10px"><span style="font-size:10px;color:#94a3b8;font-weight:700;text-transform:uppercase;">Login: </span><span style="font-size:12px;color:#4f46e5;font-weight:600;">Email address · Default password: phone number</span></div>
    </div>
    <div class="section">
      <h2>Assigned Subjects</h2>
      ${(teacher.subjects||[]).map(s=>`<span class="tag">${s}</span>`).join('') || '<span style="color:#94a3b8;font-size:12px;">No subjects assigned</span>'}
    </div>
    <div class="footer">Printed on ${new Date().toLocaleDateString('en-NG',{weekday:'long',year:'numeric',month:'long',day:'numeric'})} · EDUVA School Management System</div>
  </div>
  <script>window.onload=function(){window.print();window.onafterprint=function(){window.close();};};</script>
</body>
</html>`;
    pw.document.write(html);
    pw.document.close();
  };

  if (loading) return (
    <div className="flex items-center justify-center min-h-[60vh]">
      <Loader2 className="w-8 h-8 text-indigo-600 animate-spin" />
    </div>
  );

  if (!summary) return (
    <div className="p-8 text-center text-slate-500">Teacher not found</div>
  );

  const { teacher, assignments, my_class, student_count, class_count, recent_assignments } = summary;
  const fullName = `${teacher.first_name} ${teacher.last_name}`;
  const initials = `${teacher.first_name?.[0] || ''}${teacher.last_name?.[0] || ''}`;

  return (
    <div className="p-4 lg:p-6 max-w-5xl mx-auto" data-testid="teacher-detail-page">
      {/* Back + actions */}
      <div className="flex items-center justify-between mb-6">
        <button onClick={() => navigate('/school/teachers')}
          className="flex items-center gap-2 text-slate-500 hover:text-slate-800 text-sm font-medium transition-colors"
          data-testid="back-to-teachers">
          <ArrowLeft className="w-4 h-4" /> Back to Teachers
        </button>
        <div className="flex gap-2">
          <button
            className="flex items-center gap-1.5 border border-slate-200 text-slate-600 text-sm font-medium px-3 py-2 rounded-xl hover:bg-slate-50 transition-colors"
            data-testid="edit-teacher-btn"
            onClick={() => navigate(`/school/teachers?edit=${teacherId}`)}>
            <Edit2 className="w-3.5 h-3.5" /> Edit
          </button>
          <button onClick={handlePrint}
            className="flex items-center gap-1.5 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium px-3 py-2 rounded-xl transition-colors"
            data-testid="print-teacher-btn">
            <Printer className="w-3.5 h-3.5" /> Print Profile
          </button>
        </div>
      </div>

      {/* Hero card */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm mb-5 overflow-hidden">
        <div className="h-24 bg-gradient-to-r from-indigo-600 to-violet-600" />
        <div className="px-6 pb-5">
          <div className="flex items-end gap-4 -mt-12 mb-4">
            {teacher.avatar ? (
              <img src={teacher.avatar} alt={fullName}
                className="w-20 h-20 rounded-2xl border-4 border-white shadow-md object-cover flex-shrink-0" />
            ) : (
              <div className="w-20 h-20 rounded-2xl border-4 border-white shadow-md bg-indigo-100 flex items-center justify-center flex-shrink-0">
                <span className="text-2xl font-extrabold text-indigo-600">{initials}</span>
              </div>
            )}
            <div className="pb-1 flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="text-xl font-bold text-slate-900 truncate">{fullName}</h1>
                <Badge className={`text-xs ${teacher.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'}`}>
                  {teacher.status}
                </Badge>
                {my_class && (
                  <Badge className="text-xs bg-indigo-100 text-indigo-700">
                    Class Teacher · {my_class.name}
                  </Badge>
                )}
              </div>
              <p className="text-sm text-slate-500 mt-0.5">
                {teacher.employee_number} · {teacher.employment_type || 'Full-time'}
                {teacher.qualification && ` · ${teacher.qualification}`}
              </p>
            </div>
          </div>

          {/* Quick KPIs */}
          <div className="grid grid-cols-3 gap-3">
            <Kpi icon={GraduationCap} label="Classes" value={class_count} sub="Assigned" color="indigo" />
            <Kpi icon={Users} label="Students" value={student_count} sub="In classes" color="emerald" />
            <Kpi icon={ClipboardList} label="Assignments" value={recent_assignments.length} sub="Recent" color="violet" />
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
        <div className="flex border-b border-slate-100 px-4">
          {TABS.map(t => (
            <button key={t} onClick={() => setTab(t)}
              className={`px-4 py-3 text-sm font-semibold border-b-2 transition-colors ${tab === t ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
              data-testid={`tab-${t.toLowerCase()}`}>
              {t}
            </button>
          ))}
        </div>
        <div className="p-5">
          {tab === 'Overview' && <OverviewTab teacher={teacher} />}
          {tab === 'Classes' && <ClassesTab assignments={assignments} my_class={my_class} />}
          {tab === 'Assignments' && <AssignmentsTab assignments={recent_assignments} />}
        </div>
      </div>
    </div>
  );
}

/* ─── Tab: Overview ─────────────────────────────────────────────── */
function OverviewTab({ teacher }) {
  const fields = [
    { label: 'First Name', value: teacher.first_name },
    { label: 'Last Name', value: teacher.last_name },
    { label: 'Gender', value: teacher.gender || '—' },
    { label: 'Qualification', value: teacher.qualification || '—' },
    { label: 'Employment', value: teacher.employment_type || 'Full-time' },
    { label: 'Employee No.', value: teacher.employee_number },
    { label: 'Join Date', value: teacher.join_date || '—' },
    { label: 'Status', value: teacher.status },
  ];

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
      <div>
        <h4 className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-3">Personal Information</h4>
        <div className="space-y-0 border border-slate-100 rounded-xl overflow-hidden">
          {fields.map((f, i) => (
            <div key={f.label} className={`flex items-center justify-between px-4 py-2.5 ${i % 2 === 0 ? 'bg-slate-50/50' : 'bg-white'}`}>
              <span className="text-xs font-semibold text-slate-400">{f.label}</span>
              <span className="text-sm font-semibold text-slate-700">{f.value}</span>
            </div>
          ))}
        </div>

        {/* Login info */}
        <div className="mt-3 bg-indigo-50 border border-indigo-100 rounded-xl p-3.5">
          <div className="flex items-center gap-2 mb-2">
            <Key className="w-3.5 h-3.5 text-indigo-600" />
            <p className="text-xs font-bold text-indigo-700 uppercase tracking-wide">Login Credentials</p>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-xs text-indigo-500 font-semibold">Email (Username)</span>
            <span className="text-sm font-bold text-indigo-700 font-mono">{teacher.email}</span>
          </div>
          <div className="flex items-center justify-between mt-1">
            <span className="text-xs text-indigo-500 font-semibold">Default Password</span>
            <span className="text-xs text-indigo-400">Teacher's phone number</span>
          </div>
        </div>
      </div>

      <div>
        <h4 className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-3">Contact</h4>
        <div className="space-y-3">
          {[
            { icon: Mail, label: 'Email', value: teacher.email },
            { icon: Phone, label: 'Phone', value: teacher.phone || '—' },
            { icon: MapPin, label: 'Address', value: teacher.address || '—' },
          ].map(g => (
            <div key={g.label} className="flex items-start gap-3 bg-slate-50 rounded-xl p-3.5 border border-slate-100">
              <div className="w-8 h-8 bg-indigo-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <g.icon className="w-4 h-4 text-indigo-600" />
              </div>
              <div>
                <p className="text-xs text-slate-400 font-semibold">{g.label}</p>
                <p className="text-sm font-bold text-slate-700 mt-0.5">{g.value}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Subjects */}
        {(teacher.subjects || []).length > 0 && (
          <div className="mt-4">
            <h4 className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-2">Teaching Subjects</h4>
            <div className="flex flex-wrap gap-2">
              {teacher.subjects.map(s => (
                <span key={s} className="text-xs font-semibold bg-indigo-100 text-indigo-700 px-2.5 py-1 rounded-full">{s}</span>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/* ─── Tab: Classes ────────────────────────────────────────────────── */
function ClassesTab({ assignments, my_class }) {
  if (!assignments.length && !my_class) return (
    <div className="text-center py-10 text-slate-400">
      <GraduationCap className="w-8 h-8 mx-auto mb-2 opacity-40" />
      <p className="text-sm">No class assignments yet</p>
    </div>
  );

  // Group by class
  const byClass = {};
  assignments.forEach(a => {
    if (!byClass[a.class_id]) byClass[a.class_id] = { name: a.class_name, subjects: [] };
    byClass[a.class_id].subjects.push(a.subject_name);
  });

  return (
    <div className="space-y-3">
      {my_class && (
        <div className="bg-indigo-50 border border-indigo-100 rounded-xl p-4 flex items-center gap-3">
          <div className="w-9 h-9 bg-indigo-600 rounded-xl flex items-center justify-center flex-shrink-0">
            <GraduationCap className="w-5 h-5 text-white" />
          </div>
          <div>
            <p className="text-xs font-bold text-indigo-500 uppercase tracking-wide">Class Teacher</p>
            <p className="text-sm font-bold text-indigo-900">{my_class.name}</p>
          </div>
        </div>
      )}

      <h4 className="text-xs font-bold text-slate-500 uppercase tracking-widest mt-1 mb-2">Subject Assignments</h4>
      <div className="overflow-hidden rounded-xl border border-slate-100">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-100">
            <tr>
              <th className="text-left px-4 py-2.5 text-xs font-bold text-slate-500 uppercase">Class</th>
              <th className="text-left px-4 py-2.5 text-xs font-bold text-slate-500 uppercase">Subjects</th>
            </tr>
          </thead>
          <tbody>
            {Object.entries(byClass).map(([cid, cls], i) => (
              <tr key={cid} className={`border-b border-slate-50 ${i % 2 === 0 ? 'bg-white' : 'bg-slate-50/40'}`}>
                <td className="px-4 py-3 font-semibold text-slate-800">{cls.name}</td>
                <td className="px-4 py-3">
                  <div className="flex flex-wrap gap-1">
                    {cls.subjects.map(s => (
                      <span key={s} className="text-xs font-semibold bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded-full">{s}</span>
                    ))}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ─── Tab: Assignments ────────────────────────────────────────────── */
function AssignmentsTab({ assignments }) {
  if (!assignments.length) return (
    <div className="text-center py-10 text-slate-400">
      <ClipboardList className="w-8 h-8 mx-auto mb-2 opacity-40" />
      <p className="text-sm">No assignments created yet</p>
    </div>
  );

  return (
    <div className="overflow-hidden rounded-xl border border-slate-100">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 border-b border-slate-100">
          <tr>
            <th className="text-left px-4 py-2.5 text-xs font-bold text-slate-500 uppercase">Assignment</th>
            <th className="text-left px-4 py-2.5 text-xs font-bold text-slate-500 uppercase hidden sm:table-cell">Subject</th>
            <th className="text-center px-4 py-2.5 text-xs font-bold text-slate-500 uppercase">Submissions</th>
            <th className="text-left px-4 py-2.5 text-xs font-bold text-slate-500 uppercase hidden md:table-cell">Due</th>
          </tr>
        </thead>
        <tbody>
          {assignments.map((a, i) => {
            const isOverdue = a.due_date && new Date(a.due_date) < new Date();
            return (
              <tr key={a.id || i} className={`border-b border-slate-50 ${i % 2 === 0 ? 'bg-white' : 'bg-slate-50/40'}`}>
                <td className="px-4 py-2.5 font-semibold text-slate-800">{a.title}</td>
                <td className="px-4 py-2.5 text-slate-500 hidden sm:table-cell">{a.subject_name}</td>
                <td className="px-4 py-2.5 text-center">
                  <span className="text-xs font-bold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full">{a.submission_count}</span>
                </td>
                <td className="px-4 py-2.5 hidden md:table-cell">
                  {a.due_date ? (
                    <span className={`text-xs font-semibold ${isOverdue ? 'text-red-500' : 'text-slate-500'}`}>
                      {new Date(a.due_date).toLocaleDateString('en-NG', { day: 'numeric', month: 'short', year: '2-digit' })}
                    </span>
                  ) : '—'}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/* ─── Helper ────────────────────────────────────────────────────────── */
function Kpi({ icon: Icon, label, value, sub, color }) {
  const p = { indigo: 'bg-indigo-50 border-indigo-100 text-indigo-700', emerald: 'bg-emerald-50 border-emerald-100 text-emerald-700', violet: 'bg-violet-50 border-violet-100 text-violet-700' };
  return (
    <div className={`rounded-xl border p-3 ${p[color] || p.indigo}`}>
      <Icon className="w-4 h-4 mb-1.5 opacity-70" />
      <p className="text-lg font-extrabold leading-tight">{value}</p>
      {sub && <p className="text-xs opacity-60 leading-tight">{sub}</p>}
      <p className="text-xs font-semibold opacity-50 mt-0.5 uppercase tracking-wider">{label}</p>
    </div>
  );
}
